import type { GetStaticPropsContext } from 'next';
import dynamic from 'next/dynamic';
import Image from 'next/image';
import { uniqBy } from 'lodash-es';

import { Box, Flex, Heading, Tag, Text } from '@chakra-ui/react';
import { ArrowRightIcon } from '@udacity/chakra-uds-icons';

import { ButtonLink } from '~/components/button-link';
import { FAQSection } from '~/components/faq-section';
import { HeadingWithEmphasis } from '~/components/heading-with-emphasis';
import { StandardHeroSection } from '~/components/standard-hero';
import { AIProgramsSection } from '~/features/agentic-ai/components/ai-programs-section';
import { LimitedTimeOfferSectionProps } from '~/features/agentic-ai/components/limited-time-offer-section';
import { NewsletterSection } from '~/features/agentic-ai/components/newsletter-section';
import { TestimonialSection } from '~/features/agentic-ai/components/testimonial-section';
import { UpcomingEventsSection } from '~/features/agentic-ai/components/upcoming-events-section';
import { VideoSection } from '~/features/agentic-ai/components/video-section';
import { YOU_UTM_URL } from '~/features/agentic-ai/constants';
import { getAllMarketingEvents } from '~/features/agentic-ai/controllers/get-all-marketing-events';
import { MarketingEvent } from '~/features/agentic-ai/models/marketing-event';
import { AIResourcesSection } from '~/features/ai-campaign/components/ai-resources-section';
import { PageCategory } from '~/features/analytics/constants';
import { trackEvent } from '~/features/analytics/track-event';
import { getAllCaseStudies } from '~/features/case-study/controllers/get-all-case-studies';
import { getAllEnterpriseResources } from '~/features/enterprise-resource/controllers/get-all-enterprise-resources';
import { DEFAULT_LANGUAGE } from '~/features/internationalization/languages';
import { PageHead } from '~/features/page/components/page-head';
import { PageLayout } from '~/features/page/components/page-layout';
import { getPage } from '~/features/page/controllers/get-page';
import { Page } from '~/features/page/models/page';
import { EnterpriseCenterResource } from '~/features/page-template/templates/enterprise-resource-center/types';
import { convertResourcesToStandardFormat } from '~/features/page-template/templates/enterprise-resource-center/utils';

const LimitedTimeOfferSection = dynamic<LimitedTimeOfferSectionProps>(() =>
  import('~/features/agentic-ai/components/limited-time-offer-section').then((mod) => mod.LimitedTimeOfferSection)
);

export default function AgenticAIPage({
  page,
  aiResources,
  marketingEvents,
  isBeforeTargetDate,
}: {
  page: Page;
  aiResources: EnterpriseCenterResource[];
  marketingEvents: MarketingEvent[];
  isBeforeTargetDate: boolean;
}) {
  const heroTitle = `Introducing **Agentic AI**`;
  const heroBlurb = `Master Agentic AI from LLM reasoning to multi-agent systems, all through real-world projects, and lead the next era of AI.`;
  const videoSectionTitle = `What Is **Agentic AI** And Why Should
You Learn It Now?`;
  const testimonialSectionTitle = `Hear What Your Peers Are Saying`;
  const newsletterSectionTitle = `**Agentic AI,**
delivered to your inbox.`;
  const limitedTimeOfferTitle = `Get free 1-year access to **[You.com](${YOU_UTM_URL})'s Pro Plan** 
(**$180 value**) with a Udacity subscription`;
  const YOUTUBE_TRAILER_ID = 'YKxZsLwuAKA' as const;

  return (
    <>
      <PageHead
        page={page}
        pageCategory={PageCategory.Subcategory}
        additionalPageAnalytics={{
          label: 'Artificial Intelligence',
        }}
      />
      <PageLayout withoutVerticalPadding>
        <StandardHeroSection
          backgroundImageSlot={
            <Box inset={0} pointerEvents='none' position='absolute'>
              <Box
                inset={0}
                left='50%'
                position='absolute'
                transform='translateX(-50%)'
                sx={{
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    inset: 0,
                    display: 'block',
                    bgGradient:
                      'linear(90deg,rgba(0, 0, 0, 1) 0%, rgba(0, 0, 0, 0) 15%, rgba(0, 0, 0, 0) 85%, rgba(0, 0, 0, 1) 100%)',
                  },
                }}
                width={{
                  base: '1440px',
                }}
              >
                <Image
                  alt=''
                  loading='eager'
                  sizes='1440px'
                  src='/images/agentic-ai/hero-background.png'
                  style={{
                    objectFit: 'cover',
                    objectPosition: 'bottom',
                  }}
                  fill
                  priority
                />
              </Box>
            </Box>
          }
          containerProps={{
            bgColor: '#000',
            minHeight: '460px',
            color: 'black',
            alignItems: 'center',
          }}
          contentSlot={
            <Box position='relative' textAlign='center'>
              <Box mb={6}>
                <Tag
                  alignSelf='center'
                  bgGradient='linear(90deg, #62BBFF 0%, #006AFF 100%)'
                  border='none'
                  boxShadow='0px 0px 24px 0px #A2E7FF7A'
                  color='black'
                  fontFamily='heading'
                  fontSize='2xs'
                  fontWeight='500'
                  letterSpacing={1.1}
                  mb={4}
                  position='relative'
                  textTransform='uppercase'
                  sx={{
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      inset: 0,
                      borderRadius: 'inherit',
                      padding: '1px',
                      background: 'linear-gradient(90deg, #A2E7FF 0%, rgba(162, 231, 255, 0) 100%)',
                      mask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                      maskComposite: 'exclude',
                      pointerEvents: 'none',
                    },
                  }}
                >
                  New Nanodegree Program
                </Tag>
                <HeadingWithEmphasis
                  text={heroTitle}
                  emphasisProps={{
                    background: 'linear-gradient(89.43deg, #62BBFF 73.65%, #006AFF 97.67%)',
                    backgroundClip: 'text',
                    textShadow: '0 0 24px rgba(98, 187, 255, 0.5)',
                    sx: {
                      backgroundClip: 'text',
                      textFillColor: 'transparent',
                    },
                  }}
                  headingProps={{
                    as: 'h1',
                    fontWeight: '500 !important',
                    mb: 6,
                    size: {
                      base: 'marketing-3xl',
                      md: 'marketing-5xl',
                    },
                    whiteSpace: 'pre-wrap',
                    color: 'white',
                  }}
                />
                <Text color='white' display='inline-block' px={2} whiteSpace='pre-wrap'>
                  {heroBlurb}
                </Text>
              </Box>
              <ButtonLink
                buttonProps={{
                  rightIcon: <ArrowRightIcon h='24px' w='24px' />,
                  size: 'icon-right',
                  width: 'fit-content',
                  onClick: () =>
                    trackEvent({
                      type: 'click',
                      name: 'Agentic AI Page CTA Clicked',
                      label: 'learn_more_clicked',
                    }),
                }}
                linkProps={{
                  href: '/course/agentic-ai--nd900',
                }}
              >
                Learn More
              </ButtonLink>
            </Box>
          }
        />
        <VideoSection title={videoSectionTitle} youtubeVideoId={YOUTUBE_TRAILER_ID} />
        <AIProgramsSection
          description='Master AI with real-world projects and courses co-created with AI experts'
          programKeys={['nd900', 'nd054', 'cd14380', 'nd089', 'nd608', 'cd13764', 'nd881', 'nd088']}
          title='Start Learning AI Today'
        />
        <TestimonialSection
          title={testimonialSectionTitle}
          testimonials={[
            {
              quote: 'The Agentic AI Nanodegree program will make you ready to build Multi-Agent systems from scratch.',
              name: 'Ameya Kunjir',
              imagePath: '/images/agentic-ai/kunjir.png',
              title: 'Software Engineer',
            },
            {
              quote:
                "I'd recommend the Agentic AI Nanodegree Program because it's incredibly practical and designed to give you real-world AI skills you can apply immediately.",
              name: 'Christine Deschemin',
              imagePath: '/images/agentic-ai/deschemin.png',
              title: 'Director',
            },
            {
              quote:
                'The projects were incredibly engaging and practical... you learn how to build real-world AI agents and deploy them responsibly.',
              name: 'Jemal Tahir Mumed',
              imagePath: '/images/agentic-ai/mumed.png',
              title: 'Government Service',
            },
          ]}
        />
        <NewsletterSection
          title={newsletterSectionTitle}
          features={[
            'Be the first to know about upcoming Agentic AI courses',
            'Receive exclusive promotional opportunities',
            'Get invited to webinars with industry experts',
          ]}
        />
        <UpcomingEventsSection events={marketingEvents} title='Upcoming Agentic AI Live Events' />
        {isBeforeTargetDate && (
          <LimitedTimeOfferSection
            title={limitedTimeOfferTitle}
            features={[
              '- **Access to 31+ latest AI models** in one place, including ChatGPT, Claude, Gemini, Llama, Grok, DeepSeek, and more',
              '- **AI agents for various use cases** - or build your own and share with others',
              '- **Research & automate workflows** across public, private, and premium data sources',
              '- **Integrate with Google Drive** and upload files, up to 25MB per query',
            ].join('\n')}
          />
        )}

        <AIResourcesSection
          resources={aiResources}
          theme='agentic-blue'
          title={
            <Flex
              flexDir='column'
              gap={2}
              mb='16px'
              paddingEnd='16px'
              paddingStart='var(--carousel-offset-start, 16px)'
            >
              <Heading
                as='h2'
                color='black'
                size={{
                  base: 'marketing-3xl',
                  md: 'marketing-4xl',
                }}
              >
                The Latest AI Resources
              </Heading>
            </Flex>
          }
        />
        <FAQSection
          faq={[
            {
              _id: '1',
              question: 'What is Agentic AI?',
              answer: `Agentic AI refers to systems comprising one or more AI agents that perceive their environment, utilize AI models (such as large language models or other generative models) to reason, plan, and make decisions, take actions, and complete complex tasks with varying degrees of autonomy.`,
            },
            {
              _id: '2',
              question: 'How is Agentic AI different from other AI approaches?',
              answer: `Other AI approaches, including earlier forms of machine learning, often perform narrow, predefined tasks based on fixed rules or patterns. For example, they might excel at image recognition or text generation but lack the ability to plan and adapt to dynamic situations. While generative AI can create complex outputs like text and images, agentic AI systems are specifically designed to enable agents to operate more independently. These agents perceive their environment, employ AI models (including generative AI models) for reasoning and decision-making to determine appropriate actions, and then execute those actions to achieve complex goals. Ideally, they can also learn and adapt over time, improving their performance.`,
            },
            {
              _id: '3',
              question: 'Do I need a technical background to start learning Agentic AI?',
              answer: `Not necessarily. While tech professionals can build complex multi-agent systems using code, there are also increasingly accessible no-code or low-code solutions. This means that individuals from various backgrounds, whether you're a business leader aiming to evaluate and spearhead AI initiatives, or a non-technical expert seeking to integrate agentic AI into your daily workflows and professional practice, can find suitable ways to learn and apply this technology.`,
            },
            {
              _id: '4',
              question: 'What are real-world applications of Agentic AI?',
              answer: `Agentic AI is already being used to automate customer support, manage complex scheduling, streamline internal operations, power intelligent assistants, and scale decision-making across industries like finance, healthcare, retail, and more. It's redefining productivity by taking on multi-step tasks that previously required human coordination.`,
            },
          ]}
          titleSlot={
            <Heading
              as='h2'
              size='marketing-4xl'
              textAlign='center'
              marginBottom={{
                base: '24px',
                md: '40px',
              }}
            >
              Agentic AI FAQ
            </Heading>
          }
        />
      </PageLayout>
    </>
  );
}

export async function getStaticProps({ locale }: GetStaticPropsContext) {
  // Stop additional locale pages except the default locale from being built
  if (locale !== DEFAULT_LANGUAGE) {
    return {
      notFound: true,
    };
  }

  const [page, allCaseStudies, allEnterpriseResources, allMarketingEvents] = await Promise.all([
    getPage('/course-collection/agentic-ai'),
    getAllCaseStudies(),
    getAllEnterpriseResources(),
    getAllMarketingEvents(),
  ]);

  if (!page) {
    return {
      notFound: true,
    };
  }

  const combinedResources = [...allEnterpriseResources, ...allCaseStudies]
    .sort((a, b) => b._updatedAt.localeCompare(a._updatedAt))
    .filter((resource) => !resource.excludeFromResourceCenter);

  const enterpriseCenterResources = uniqBy(convertResourcesToStandardFormat(combinedResources), (item) => item._id);

  return {
    props: {
      page,
      aiResources: enterpriseCenterResources.filter((resource) =>
        resource.knowledgeAreas?.includes('artificialIntelligence')
      ),
      marketingEvents: allMarketingEvents,
      isBeforeTargetDate: false,
    },
  };
}
